import { useGetAvailablePlansSuspense } from "@/orval/gen"
import { Group, NumberInput, Textarea } from "@mantine/core"
import {
  ActionIcon,
  SegmentedControl,
  Select,
  Stack,
  Text,
} from "@mantine/core"
import { DatePickerInput, DatesRangeValue } from "@mantine/dates"
import { useForm } from "@mantine/form"
import { IconCopy } from "@tabler/icons-react"
import { useTranslation } from "react-i18next"

type Format = "PDF" | "Word"
type Mode = "date-range" | "offset"

function getExportUrl(values: FormValues, mode: Mode) {
  const { planId, start, end, format } = values

  const params = new URLSearchParams({ planId, format, download: "false" })

  if (mode === "date-range") {
    params.set("start", start)
    params.set("end", end)
  } else {
    // Parameternamen ggf. an den Endpunkt anpassen
    params.set("start", String(start))
    params.set("end", String(end))
  }

  return `${window.location.origin}/app/art/schedules/export?${params}`
}

interface FormValues {
  planId: string
  start: string
  end: string
  format: Format
}

export function ExportLinkGenerator({
  initialValues,
}: {
  initialValues: FormValues
}) {
  const { t } = useTranslation("app_server")
  const { data: plans } = useGetAvailablePlansSuspense()
  const plansOptions =
    plans?.map((plan) => ({ label: plan.name, value: plan.id?.toString() })) ??
    []
  const form = useForm({
    initialValues,
  })

  const [mode, setMode] = React.useState<Mode>("date-range")
  const dateRange: DatesRangeValue<string> = [
    form.values.start,
    form.values.end,
  ]

  // on mode change set form values to valid defaults
  React.useEffect(() => {
    if (mode === "date-range") {
      form.setValues({ start: initialValues.start, end: initialValues.end })
    }
    if (mode === "offset") {
      form.setValues({ start: "0", end: "0" })
    }
  }, [mode])

  return (
    <Stack>
      <Select
        label="Plan"
        data={plansOptions}
        {...form.getInputProps("planId")}
      />
      <SegmentedControl
        data={[
          { label: t("Schedules_FixedPeriod"), value: "date-range" },
          { label: t("Schedules_Offset"), value: "offset" },
        ]}
        value={mode}
        onChange={setMode}
      />
      {mode === "date-range" ? (
        <DatePickerInput
          allowSingleDateInRange
          type="range"
          value={dateRange}
          onChange={([start, end]) => {
            form.setValues({
              start: start ?? "",
              end: end ?? "",
            })
          }}
        />
      ) : (
        <>
          <Text>{t("Schedules_OffsetDescription")}</Text>
          <Group wrap="nowrap">
            <NumberInput label={t("Start")} {...form.getInputProps("start")} />
            <NumberInput label={t("End")} {...form.getInputProps("end")} />
          </Group>
        </>
      )}
      <SegmentedControl
        data={["PDF", "Word"]}
        {...form.getInputProps("format")}
      />
      <Textarea
        autosize
        readOnly
        label="Export URL"
        value={getExportUrl(form.values, mode)}
        rightSection={
          <ActionIcon
            variant="subtle"
            size="xs"
            onClick={() => {
              navigator.clipboard.writeText(getExportUrl(form.values, mode))
            }}
          >
            <IconCopy />
          </ActionIcon>
        }
      />
    </Stack>
  )
}
